import React, { forwardRef, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { bool, func, object, oneOf, string } from "prop-types";

import componentSizes from "../../constants/componentSizes";
import Field from "../../form/Field";
import { TextInput } from "../../form/TextField";
import { VisuallyHidden } from "../../style-utilities/visually-hidden";
import SuggestedEmail from "./components/SuggestedEmail";
import querySuggestedEmail from "./utilities/querySuggestedEmail";

const EmailField = forwardRef(
  (
    {
      className,
      defaultValue,
      disabled,
      error,
      helper,
      id,
      inputOptions,
      isValid,
      label,
      name,
      onChange,
      onSuggestedEmailClick,
      onSuggestedEmailRendered,
      required,
      size,
      value,
      ...passThroughProps
    },
    ref
  ) => {
    const emailInputRef = useRef(null);
    const suggestedEmailHelperID = "suggested-email-helper-id";

    const [suggestedEmailVal, setSuggestedEmailVal] = useState("");

    const getSuggestedEmail = email => {
      const suggestedEmail = querySuggestedEmail(email);

      if (suggestedEmail) {
        setSuggestedEmailVal(suggestedEmail);
        onSuggestedEmailRendered();
      }
    };

    const debouncedGetSuggestedEmail = debounce(getSuggestedEmail, 1000);

    const handleChange = event => {
      const nextEmail = event.target.value;

      setSuggestedEmailVal("");
      debouncedGetSuggestedEmail(nextEmail);

      onChange(event, nextEmail);
    };

    const handleSuggestedEmailClick = e => {
      setSuggestedEmailVal("");

      emailInputRef.current.value = suggestedEmailVal;
      emailInputRef.current.focus();

      onSuggestedEmailClick(e, suggestedEmailVal);

      /**
       * Ensure parent components monitoring changes
       * have the latest email address present in the input.
       */
      onChange(
        { target: { name, value: suggestedEmailVal } },
        suggestedEmailVal
      );
    };

    const SuggestedEmailHelper = (
      <span aria-live="polite" id={suggestedEmailHelperID}>
        <SuggestedEmail
          onClick={handleSuggestedEmailClick}
          suggestedEmail={suggestedEmailVal}
        />
        <VisuallyHidden>
          Clicking the button beneath this email input will make{" "}
          {suggestedEmailVal} the value of this input.
        </VisuallyHidden>
      </span>
    );

    const helperTextNode = suggestedEmailVal ? SuggestedEmailHelper : helper;

    return (
      <Field
        ref={ref}
        className={className}
        error={error}
        helper={helperTextNode}
        id={id}
        isValid={isValid}
        label={label}
        {...passThroughProps}
      >
        <TextInput
          ref={emailInputRef}
          aria-owns={suggestedEmailVal ? suggestedEmailHelperID : null}
          defaultValue={defaultValue}
          disabled={disabled}
          isValid={isValid}
          name={name}
          onChange={handleChange}
          required={required}
          size={size}
          type="email"
          value={value}
          {...inputOptions}
        />
      </Field>
    );
  }
);

EmailField.displayName = "EmailField";

EmailField.propTypes = {
  className: string,
  // eslint-disable-next-line react/require-default-props
  defaultValue: string,
  disabled: bool,
  error: string,
  helper: string,
  hint: string.isRequired,
  id: string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  inputOptions: object,
  isValid: bool,
  label: string,
  name: string,
  onChange: func,
  onSuggestedEmailClick: func,
  onSuggestedEmailRendered: func,
  required: bool,
  size: oneOf([componentSizes.SM, componentSizes.MD, componentSizes.LG]),
  // eslint-disable-next-line react/require-default-props
  value: string
};

EmailField.defaultProps = {
  className: "",
  disabled: false,
  error: "",
  isValid: true,
  helper: "",
  inputOptions: {},
  label: "Email",
  name: "",
  onChange: () => {},
  onSuggestedEmailClick: () => {},
  onSuggestedEmailRendered: () => {},
  required: false,
  size: componentSizes.MD
};

export default EmailField;
